import { Button, DialogTrigger, makeStyles, Spinner } from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import { Dismiss20Filled, FolderArrowRight20Regular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteReturnsFromFolder, useGetAllFolders } from '../../../api/report/report';
import { ISelectedTaxReturnsAndFilters } from '../../../data-types/exportReports';
import { IFilterItem } from '../../../data-types/filterItem';
import { useReportsTableFiltering } from '../../../hooks/reportsTable/useReportsTableFiltering';
import { useFeedbackMessages } from '../../../hooks/useFeedbackMessages';
import { useFunctionalAccess } from '../../../hooks/useFunctionalAccess';
import { useGridState } from '../../../hooks/useGridState';
import CartButton from '../../common/CartButton';
import ExportReportButton from '../../common/ExportReportButton';
import FunctionalAccessDisableWrapper from '../../common/FunctionalAccessDisableWrapper';
import ConfirmationDialogV9 from '../../dialog/ConfirmationDialogV9';
import AddToFolderModal from '../../folders/AddToFolderModal';
import HelpButton from '../../help/HelpButton';
import FoldersListFilter from './FoldersListFilter';

export interface IFilterBarProps {
    folders: IFilterItem[];
    disabled: boolean;
    updateFolderList?: () => Promise<any>;
    getReportsToExport: () => ISelectedTaxReturnsAndFilters;
    disableActionButtons?: boolean;
    isBulkEditActive?: boolean;
}

const useFilterBarStyles = makeStyles({
    rootStyle: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 'var(--spacingHorizontalS)',
        margin: '12px 15px 0',
        boxSizing: 'border-box',
        paddingTop: 'var(--spacingVerticalM)',
        paddingBottom: 'var(--spacingVerticalM)',
        paddingLeft: 'var(--spacingHorizontalL)',
        paddingRight: 'var(--spacingHorizontalL)',
        backgroundColor: 'var(--colorNeutralBackground1)',
        boxShadow: 'var(--shadow4)',
    },
    contentStyle: {
        display: 'flex',
        flexDirection: 'row',
        gap: '15px',
        alignItems: 'flex-end',
        flex: 1,
        minWidth: 0,
        '& > div': {
            gap: '12px 15px',
        },
    },
    filterPanelStyle: {
        position: 'relative',
    },
    actionsStyle: {
        display: 'flex',
        flexDirection: 'row',
        gap: '15px',
        alignItems: 'center',
    },
});

const FilterBar: React.FC<IFilterBarProps> = ({
    folders,
    disabled,
    getReportsToExport,
    updateFolderList,
    disableActionButtons = false,
    isBulkEditActive = false,
}) => {
    const { t } = useTranslation();
    const { displayFeedbackMessage } = useFeedbackMessages();
    const { grid } = useGridState();
    const queryClient = useQueryClient();
    const [isFolderModalVisible, { toggle: toggleFolderModalVisibility }] = useBoolean(false);
    const [removeFromFolderConfirmationVisible, { setTrue: showRemoveFromFolderConfirmation, setFalse: hideRemoveFromFolderConfirmation }] =
        useBoolean(false);
    const { functionalAccess } = useFunctionalAccess();
    const selectedReturns = getReportsToExport().selectedTaxReturns?.map((r) => r.id ?? 0) || [];
    const { folderFilterName, selectedFolderFilter, setSelectedFolderFilter } = useReportsTableFiltering();
    const { data: folderItems, isLoading: isLoadingGetAllFolders, queryKey: getAllFoldersQuery } = useGetAllFolders();

    const { mutateAsync: deleteReturnsFromFolder, isLoading: isLoadingDeleteReturnsFromFolder } = useDeleteReturnsFromFolder({
        mutation: {
            onSuccess: () => {
                queryClient.invalidateQueries(getAllFoldersQuery);
                hideRemoveFromFolderConfirmation();
                displayFeedbackMessage({ type: 'success', message: t('calculationRemovedSuccessMessage') });
            },
        },
    });

    const { filterPanelStyle, rootStyle, contentStyle, actionsStyle } = useFilterBarStyles();

    const isFilteredByFolder = useMemo(() => {
        return !!selectedFolderFilter;
    }, [selectedFolderFilter]);

    const onFolderChange = useCallback(
        (filteredItems: IFilterItem[], _filterName: string) => {
            setSelectedFolderFilter(filteredItems[0]);
        },
        [setSelectedFolderFilter]
    );

    const removeSelectedReturnsFromFolder = async () => {
        const selectedReturns = getReportsToExport().selectedTaxReturns ?? [];
        const selectedIds = selectedReturns.map((r) => r.id ?? 0);

        await deleteReturnsFromFolder({
            data: {
                folderKey: parseInt(selectedFolderFilter?.key ?? '0'),
                keys: selectedIds,
            },
        });

        deleteRecordsFromGridById(selectedIds);
    };

    const deleteRecordsFromGridById = (ids: number[] | null | undefined) => {
        if (grid && grid.current && ids) {
            ids.forEach((id) => {
                grid.current?.deleteRecord('id', id);
            });
        }
    };

    return (
        <div className={filterPanelStyle}>
            <div className={rootStyle}>
                <div className={contentStyle}>
                    <FunctionalAccessDisableWrapper hasFunctionalAccess={functionalAccess?.folderDropdownEnabled}>
                        <FoldersListFilter
                            folders={folders}
                            isFiltered={isFilteredByFolder}
                            filterName={folderFilterName}
                            label='allCalculations'
                            selectedFolderFilter={selectedFolderFilter}
                            showSingleSelect={true}
                            onFolderChange={onFolderChange}
                            disabled={disabled || isBulkEditActive}
                            updateFolderList={updateFolderList}
                        />
                    </FunctionalAccessDisableWrapper>

                    <FunctionalAccessDisableWrapper
                        hasFunctionalAccess={functionalAccess?.folderDropdownEnabled && functionalAccess?.editFolderButtonEnabled}
                    >
                        <Button
                            aria-label={t('addToFolder').toString()}
                            icon={<FolderArrowRight20Regular />}
                            onClick={toggleFolderModalVisibility}
                            disabled={disabled || disableActionButtons || isBulkEditActive}
                            disabledFocusable={disabled || disableActionButtons || isBulkEditActive}
                        >
                            {t('addToFolder').toString()}
                        </Button>
                    </FunctionalAccessDisableWrapper>
                    {isFilteredByFolder && (
                        <FunctionalAccessDisableWrapper hasFunctionalAccess={functionalAccess?.editFolderButtonEnabled}>
                            <Button
                                aria-label={t('removeFromFolder').toString()}
                                icon={<Dismiss20Filled />}
                                onClick={showRemoveFromFolderConfirmation}
                                disabled={disabled || disableActionButtons || isBulkEditActive}
                                disabledFocusable={disabled || disableActionButtons || isBulkEditActive}
                            >
                                {t('removeFromFolder').toString()}
                            </Button>
                        </FunctionalAccessDisableWrapper>
                    )}
                </div>

                <div className={actionsStyle}>
                    <CartButton
                        getReportsToExport={getReportsToExport}
                        disabled={disabled || disableActionButtons || isBulkEditActive}
                        disableMenu={isBulkEditActive}
                    />
                    <ExportReportButton
                        getReportsToExport={getReportsToExport}
                        disabled={disabled || disableActionButtons || isBulkEditActive}
                        includeExportList={true}
                    ></ExportReportButton>
                    <HelpButton />
                </div>
            </div>
            <AddToFolderModal
                isOpen={isFolderModalVisible}
                folders={folderItems ?? []}
                selectedReturns={selectedReturns}
                isLoadingGetAllFolders={isLoadingGetAllFolders}
                getAllFoldersQuery={getAllFoldersQuery}
                onClose={toggleFolderModalVisibility}
            ></AddToFolderModal>
            {removeFromFolderConfirmationVisible && (
                <ConfirmationDialogV9
                    onDismiss={hideRemoveFromFolderConfirmation}
                    content={t('removeFromFolderDialogContent')}
                    title={t('removeFromFolderDialogHeading')}
                    buttons={
                        <>
                            <DialogTrigger>
                                <Button
                                    aria-label={t('cancel').toString()}
                                    onClick={hideRemoveFromFolderConfirmation}
                                    disabled={isLoadingDeleteReturnsFromFolder}
                                    data-analytics-button-name='confirmation_dialog_cancel'
                                >
                                    {t('cancel').toString()}
                                </Button>
                            </DialogTrigger>
                            <Button
                                appearance='primary'
                                aria-label={t('remove').toString()}
                                onClick={removeSelectedReturnsFromFolder}
                                disabled={Boolean(isLoadingDeleteReturnsFromFolder)}
                                data-analytics-button-name='confirmation_dialog_save'
                            >
                                {isLoadingDeleteReturnsFromFolder ? t('removing').toString() : t('remove').toString()}
                                {isLoadingDeleteReturnsFromFolder && <Spinner size='extra-tiny' />}
                            </Button>
                        </>
                    }
                />
            )}
        </div>
    );
};

export default FilterBar;
